import React, { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { payApple } from '../../api/payApple'
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation'
import { extraFieldShowFilter } from '../../utils/filters'
import { paymentErrorHandler } from '../../utils/paymentErrorHandler'
import { PaymentMethodType, PaymentPageInfo } from '../../types/paymentPageInfo'
import { getPaymentMethodExtraField } from '../../types/paymentMethod'
import Money from '../../types/Money'
import ExtraFields from '../ExtraFields/ExtraFields'
import SubmitButton from '../SubmitButton/SubmitButton'
import styles from './PaymentMethod.module.css'

interface ApplePaymentProps {
    paymentPageInfo: PaymentPageInfo
    paymentMethod: PaymentMethodType
    money?: Money
    email: string
    setParentError: (field: string, message: string) => void
    validateParentFields: (fields: Record<string, string>) => boolean
    setPaymentError: Dispatch<SetStateAction<boolean>>
}

const ApplePaymentMethod = ({
    paymentPageInfo,
    paymentMethod,
    money,
    email,
    setParentError,
    validateParentFields,
    setPaymentError,
}: ApplePaymentProps) => {
    const navigate = useNavigate()

    const [extraFieldsData, setExtraFieldsData] = useState<Record<string, string>>(() => {
        const initialData: Record<string, string> = {}

        paymentMethod.extraFields.forEach(field => {
            initialData[field.name] = field.value || ''
        })

        return initialData
    })

    const [isSubmitting, setIsSubmitting] = useState(false)

    const validationRules = useMemo(() => {
        const rules: ValidationRules = {}

        paymentMethod.extraFields
            .filter(extraFieldShowFilter)
            .forEach(field => {
                rules[field.name] = {
                    required: true,
                    validation: getPaymentMethodExtraField(field.name)?.validation,
                }
            })

        return rules
    }, [paymentMethod.extraFields])

    const { errors, setError, removeError, validateAllFields } = useFormValidation(validationRules)

    const handleExtraFieldChange = (name: string, value: string) => {
        setExtraFieldsData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validParentFields = validateParentFields({ email: email, amount: money?.amount || '' })
        const validThisFields = validateAllFields({ ...extraFieldsData })

        if (!validParentFields || !validThisFields) {
            return
        }

        setIsSubmitting(true)

        try {
            const transaction = await payApple(paymentPageInfo.id, { email: email, ...extraFieldsData }, money)

            navigate(`/payment/${paymentPageInfo.id}/apple/${transaction.transactionId}`)
        } catch (error) {
            paymentErrorHandler(error, setError, setParentError, setPaymentError)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} className={styles.form}>
                {paymentMethod.extraFields.length > 0 && (
                    <ExtraFields
                        extraFields={paymentMethod.extraFields}
                        data={extraFieldsData}
                        errors={errors}
                        handleExtraFieldChange={handleExtraFieldChange}
                    />
                )}

                <SubmitButton isSubmitting={isSubmitting} />
            </form>
        </div>
    )
}

export default ApplePaymentMethod
